"use client"

import { useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate, useParams } from "react-router-dom"
import { fetchEmailVerifiedStatus } from "../Slice/EmailVerification/EmailVerificationSlice"
import {
    Box,
    Heading,
    Text,
    Button,
    VStack,
    Center,
    Spinner,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
    Flex,
    Icon,
    useColorModeValue,
    Divider,
    ScaleFade,
} from "@chakra-ui/react"
import { CheckCircleIcon, WarningIcon, ArrowForwardIcon } from "@chakra-ui/icons"

const VerifiedPage = () => {
    const { token } = useParams()
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const emailVerification = useSelector(state => state.emailVerification)

    // Theme colors
    const cardBg = useColorModeValue("white", "gray.800")
    const headingColor = useColorModeValue("blue.600", "blue.300")
    const textColor = useColorModeValue("gray.600", "gray.300")
    const successIconBgColor = useColorModeValue("green.50", "green.900")
    const errorIconBgColor = useColorModeValue("red.50", "red.900")

    useEffect(() => {
        if (token) {
            dispatch(fetchEmailVerifiedStatus(token))
        } else {
            console.error("No token provided in URL")
        }
    }, [token, dispatch])

    // Debug log to see current state
    useEffect(() => {
        console.error("Email Verification State:", emailVerification)
    }, [emailVerification])

    const isLoading = emailVerification.loading
    const isVerified = emailVerification.verified
    const error = emailVerification.error
    const status = emailVerification.status

    const getStatusMessage = () => {
        if (isLoading) return "Verifying your email..."
        if (error) return error
        if (isVerified) return "Your email has been successfully verified!"
        if (status === "failed") return "Verification failed"
        return "Checking verification status..."
    }

    return (
        <div className="bg-mainBg p-3">
            <div className="px-2 sm:px-4 sm:py-4 min-h-screen">
                <div className="rounded-xl sm:px-28">
                    <Center h="full">
                        <ScaleFade initialScale={0.9} in={true}>
                            <Box
                                bg={cardBg}
                                p={6}
                                borderRadius="2xl"
                                width="100%"
                                position="relative"
                                overflow="hidden"
                                maxWidth="500px"
                            >
                                <VStack align="center" spacing={4}>
                                    <Heading size="lg" textAlign="center" color={headingColor} fontWeight="semi-bold">
                                        Email Verification
                                    </Heading>

                                    <Divider />

                                    {/* Loading State */}
                                    {isLoading && (
                                        <VStack spacing={6} py={8}>
                                            <Spinner
                                                thickness="4px"
                                                speed="0.65s"
                                                emptyColor="gray.200"
                                                color="blue.500"
                                                size="xl"
                                            />
                                            <Text color={textColor} fontSize="md">
                                                {getStatusMessage()}
                                            </Text>
                                        </VStack>
                                    )}

                                    {/* Error State */}
                                    {!isLoading && error && (
                                        <VStack spacing={6} w="full">
                                            <Box p={5} borderRadius="full" bg={errorIconBgColor}>
                                                <Icon as={WarningIcon} w={16} h={16} color="red.500" />
                                            </Box>

                                            <Alert status="error" variant="subtle" borderRadius="lg">
                                                <AlertIcon />
                                                <Box>
                                                    <AlertTitle fontSize="lg">Verification Failed</AlertTitle>
                                                    <AlertDescription maxWidth="sm" fontSize="md">
                                                        {getStatusMessage()}
                                                    </AlertDescription>
                                                </Box>
                                            </Alert>

                                            <Text color={textColor} textAlign="center" fontSize="sm">
                                                Please try using the verification link again or contact support.
                                            </Text>
                                        </VStack>
                                    )}

                                    {/* Success State */}
                                    {!isLoading && isVerified && (
                                        <VStack spacing={6} w="full">
                                            <Box p={5} borderRadius="full" bg={successIconBgColor}>
                                                <Icon as={CheckCircleIcon} w={16} h={16} color="green.500" />
                                            </Box>

                                            <Alert status="success" variant="subtle" borderRadius="lg">
                                                <AlertIcon />
                                                <Box>
                                                    <AlertTitle fontSize="lg">Email Verified!</AlertTitle>
                                                    <AlertDescription fontSize="md">
                                                        {getStatusMessage()}
                                                    </AlertDescription>
                                                </Box>
                                            </Alert>

                                            <Text color={textColor} textAlign="center" fontSize="sm">
                                                You can now access all features with your verified email address.
                                            </Text>
                                        </VStack>
                                    )}

                                    {/* Unknown State */}
                                    {!isLoading && !error && !isVerified && (
                                        <VStack spacing={6} w="full">
                                            <Box p={5} borderRadius="full" bg={errorIconBgColor}>
                                                <Icon as={WarningIcon} w={16} h={16} color="orange.500" />
                                            </Box>

                                            <Alert status="warning" variant="subtle" borderRadius="lg">
                                                <AlertIcon />
                                                <Box>
                                                    <AlertTitle fontSize="lg">Verification Status Unknown</AlertTitle>
                                                    <AlertDescription fontSize="md">
                                                        Unable to determine verification status.
                                                    </AlertDescription>
                                                </Box>
                                            </Alert>
                                        </VStack>
                                    )}

                                    <Button
                                        colorScheme="blue"
                                        size="lg"
                                        height="60px"
                                        width="full"
                                        onClick={() => navigate("/")}
                                        rightIcon={<ArrowForwardIcon />}
                                        _hover={{
                                            transform: "translateY(-3px)",
                                            boxShadow: "xl",
                                        }}
                                        transition="all 0.3s"
                                        borderRadius="lg"
                                        fontWeight="bold"
                                        fontSize="md"
                                        mt={4}
                                    >
                                        Go to Home
                                    </Button>


                                </VStack>
                            </Box>
                        </ScaleFade>
                    </Center>
                </div>
            </div>
        </div>
    )
}

export default VerifiedPage